import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useT } from '../../i18n/I18nContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c868a3fd-bb7d-4698-a989-0d713ad2f0ec/artifacts/skn71kk2_WhatsApp%20Image%202026-01-16%20at%209.58.41%20AM%20%281%29.jpeg";

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useT();

  const navLinks = [
    { key: 'home', label: t('nav.home'), path: '/' },
    { key: 'about', label: t('nav.about'), path: '/about' },
    { key: 'smartechs', label: t('nav.smartechs'), path: '/smartechs' },
    { key: 'trainings', label: t('nav.trainings'), path: '/trainings' },
    { key: 'impact', label: t('nav.impact'), path: '/impact' },
    { key: 'news', label: t('nav.news'), path: '/news' },
    { key: 'contact', label: t('nav.contact'), path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const LanguageToggle = ({ className = '' }) => (
    <div
      className={`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-0.5 ${className}`}
      role="group"
      aria-label={t('nav.languageToggleAria')}
      data-testid="language-toggle"
    >
      <Globe className="h-4 w-4 text-slate-500 ml-2" aria-hidden="true" />
      <button
        type="button"
        onClick={() => setLang('en')}
        className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${lang === 'en' ? 'bg-primary text-white' : 'text-slate-600 hover:text-primary'}`}
        aria-pressed={lang === 'en'}
        data-testid="lang-en"
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLang('fr')}
        className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${lang === 'fr' ? 'bg-primary text-white' : 'text-slate-600 hover:text-primary'}`}
        aria-pressed={lang === 'fr'}
        data-testid="lang-fr"
      >
        FR
      </button>
    </div>
  );

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-md" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="SMART Centre Cameroon Logo"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-primary'
                    : 'text-slate-700 hover:text-primary'
                }`}
                data-testid={`nav-link-${link.key}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language toggle + CTA Button - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageToggle />
            <Link
              to="/trainings"
              className="bg-secondary text-white hover:bg-secondary-hover rounded-full px-6 py-2.5 font-semibold text-sm transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
              data-testid="nav-enroll-btn"
            >
              {t('nav.enrollNow')}
            </Link>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageToggle />
            <button
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-slate-700" />
              ) : (
                <Menu className="h-6 w-6 text-slate-700" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-accent text-primary'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.key}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4">
                <Link
                  to="/trainings"
                  className="block w-full text-center bg-secondary text-white hover:bg-secondary-hover rounded-full px-6 py-3 font-semibold transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid="mobile-enroll-btn"
                >
                  {t('nav.enrollNow')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
